import _ from 'lodash';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE, PUSH_WORKBENCH } from '@/core/flux.service';
import { ITEM_TYPES } from '@/homescreen/homescreen.constants';
import { CREATED_BY_SEEQ_WORKBENCH, LIMIT_SIZE_1000, MAX_NAME_LENGTH } from '@/main/app.constants';
import { getNavigate, getWorksheetLink } from '@/main/routing.utilities';
import { addFilterForTableDefinitionSubTypes } from '@/search/search.utilities';
import { emitWorkbook, emitWorkbooks, onWorkbook } from '@/services/notifier.service';
import { closeInvestigationTool } from '@/toolSelection/investigate.actions';
import { fetchEnrichedSharedTemplateItems, fetchEnrichedTemplateItems } from '@/tools/models/template.actions';
import { doTrack } from '@/track/trackProxy.service';
import {
  addRecentlyAccessed as addRecentlyAccessedHomescreen,
  getWorkbook,
  setProjectType,
  setProperty,
} from '@/utilities/homescreen.utilities';
import { addAssetsProperty } from '@/utilities/httpHelpers.utilities';
import { errorToast, successToast, warnToast } from '@/utilities/toast.utilities';
import { getNextName } from '@/utilities/utilities';
import { isEditWorkbookMode } from '@/utilities/workbookMode.utilities';
import { LOAD_STATUS, WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { fetchAndAddTableTypePropertyToItems } from '@/workbook/workbook.utilities';
import {
  DEFAULT_WORKSHEET_NAME,
  archiveWorksheet as archiveWorksheetUtil,
  createWorksheet,
  getWorksheet,
  moveWorksheet as moveWorksheetUtil,
  restoreWorksheet as restoreWorksheetUtils,
  setName as setNameUtil,
  toWorksheet,
} from '@/worksheets/worksheets.utilities';
import { clearWorkstepAction } from '@/worksteps/worksteps.actions';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTreesApi } from '@seeqdev/api/endpoint/TreesApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { type TableDefinitionOutputV1, TableTypeEnum } from '@seeqdev/api/model/TableDefinitionOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import type { StoreWorkbookWithWorksheets, VantageWorkbook } from '@/worksheet/worksheet.types';
import type { AssetTreeOutputV1 } from '@seeqdev/api/model/AssetTreeOutputV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';
import type { ProjectTypeEnum } from '@seeqdev/api/model/ProjectInputV1';

/**
 * Flux workbook actions
 */

/**
 * Returns the name, if specified, or computes one, which is the next available number, to use for the worksheet.
 *
 * @param name - Optional name for the worksheet. If no name is provided, a name is
 *   automatically computed.
 * @param prefix - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 * @return Worksheet name to use
 */
export function computeWorksheetName({ name = '', prefix = '' } = {}): string {
  if (name) {
    return name;
  }

  return getNextName(
    sqWorkbookStore.worksheets.map((ws) => ws.name),
    prefix,
    true,
  );
}

/**
 * Makes an asynchronous call to load the pinned items.
 * Before load the loading status will be set to LOADING and when loading is finished it either set to LOADED or
 * to ERROR
 *
 * @param workbookId - The ID of the workbook for which the pinned items are requested to be loaded.
 */
export async function fetchPinnedItems(workbookId: string) {
  flux.dispatch(
    'WORKBOOK_SET_PINNED_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const items = (await sqItemsApi
      .getPinnedItems({ sourceId: workbookId })
      .then(({ data }) => addAssetsProperty(data))
      .then(_.property('items'))) as ItemSearchPreviewV1[];

    const updatedItems = await fetchAndAddTableTypePropertyToItems(items);
    flux.dispatch('WORKBOOK_SET_PINNED', { items: updatedItems }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_PINNED_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch (e) {
    flux.dispatch(
      'WORKBOOK_SET_PINNED_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

/**
 * Makes an asynchronous call to load the recently accessed items.
 * Before load the loading status will be set to LOADING and when loading is finished it either set to LOADED or
 * to ERROR
 *
 * @param workbookId - The ID of the workbook for which the recently accessed items are requested to be
 * loaded.
 */
export function fetchRecentlyAccessedItems(workbookId: string) {
  flux.dispatch(
    'WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  return sqWorkbooksApi
    .getRecentlyAccessed({ workbookId })
    .then(({ data }) => addAssetsProperty(data))
    .then(_.property('items'))
    .then((items) => {
      flux.dispatch('WORKBOOK_SET_RECENTLY_ACCESSED', { items }, PUSH_IGNORE);
      flux.dispatch(
        'WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS',
        {
          loadStatus: LOAD_STATUS.LOADED,
        },
        PUSH_IGNORE,
      );
    })
    .catch(() => {
      flux.dispatch(
        'WORKBOOK_SET_RECENTLY_ACCESSED_LOAD_STATUS',
        {
          loadStatus: LOAD_STATUS.ERROR,
        },
        PUSH_IGNORE,
      );
    });
}

async function fetchTableDefinitionItems(workbookIds: string[], type: 'Scaling' | 'Item', ignoreScope = false) {
  const types = [SeeqNames.Types.TableDefinition];
  const { data } = await sqItemsApi.searchItems({
    types,
    filters: addFilterForTableDefinitionSubTypes(types, [], type) as string[],
    scope: ignoreScope ? undefined : workbookIds,
    limit: LIMIT_SIZE_1000,
    includeProperties: [SeeqNames.Properties.TableType],
  });
  return data.items;
}

export function fetchAssets(
  workbookIds: string[],
  filterType?: string,
  extraHttpRequestConfig?: SeeqAxiosRequestConfig,
) {
  return sqTreesApi.getTreeRootNodes(
    {
      scope: _.compact(workbookIds),
      properties: filterType ? [`${SeeqNames.Properties.TreeType}=${filterType}`] : undefined,
      limit: LIMIT_SIZE_1000,
    },
    extraHttpRequestConfig,
  );
}

export async function fetchAssetTrees(workbookIds: string[]) {
  flux.dispatch(
    'WORKBOOK_SET_ASSET_TREES_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const data: AssetTreeOutputV1 = (await fetchAssets(workbookIds)).data;
    flux.dispatch('WORKBOOK_SET_ASSET_TREES', { items: data.children }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_ASSET_TREES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_ASSET_TREES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export async function fetchAssetGroups(workbookIds: string[]) {
  flux.dispatch(
    'WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const { data } = await fetchAssets(workbookIds, CREATED_BY_SEEQ_WORKBENCH);
    flux.dispatch('WORKBOOK_SET_ASSET_GROUPS', { items: data.children }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_ASSET_GROUPS_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export async function fetchScalingTables(workbookIds: string[], ignoreScope = false) {
  flux.dispatch(
    'WORKBOOK_SET_TABLE_DEFINITIONS_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const definitions = await fetchTableDefinitionItems(workbookIds, 'Scaling', ignoreScope);
    flux.dispatch('WORKBOOK_SET_TABLE_DEFINITIONS', { items: definitions }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_TABLE_DEFINITIONS_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_TABLE_DEFINITIONS_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export async function fetchTemplates(workbookIds: string[]) {
  flux.dispatch(
    'WORKBOOK_SET_TEMPLATES_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const templates = await fetchEnrichedTemplateItems(workbookIds);
    flux.dispatch('WORKBOOK_SET_TEMPLATES', { items: templates }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_TEMPLATES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_TEMPLATES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export async function fetchSharedTemplates(workbookId: string) {
  flux.dispatch(
    'WORKBOOK_SET_SHARED_TEMPLATES_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const [sharedTemplates, industryTemplates] = await Promise.all([
      fetchEnrichedSharedTemplateItems(SeeqNames.LocalDatasources.Templates.User.DatasourceId, workbookId),
      fetchEnrichedSharedTemplateItems(SeeqNames.LocalDatasources.Templates.Industry.DatasourceId, workbookId),
    ]);
    const templates = [...sharedTemplates, ...industryTemplates];
    flux.dispatch('WORKBOOK_SET_SHARED_TEMPLATES', { items: templates }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_SHARED_TEMPLATES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_SHARED_TEMPLATES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export async function fetchItemTables(workbookIds: string[]) {
  flux.dispatch(
    'WORKBOOK_SET_ITEM_TABLES_LOAD_STATUS',
    {
      loadStatus: LOAD_STATUS.LOADING,
    },
    PUSH_IGNORE,
  );
  try {
    const definitions = await fetchTableDefinitionItems(workbookIds, 'Item');
    flux.dispatch('WORKBOOK_SET_ITEM_TABLES', { items: definitions }, PUSH_IGNORE);
    flux.dispatch(
      'WORKBOOK_SET_ITEM_TABLES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.LOADED,
      },
      PUSH_IGNORE,
    );
  } catch {
    flux.dispatch(
      'WORKBOOK_SET_ITEM_TABLES_LOAD_STATUS',
      {
        loadStatus: LOAD_STATUS.ERROR,
      },
      PUSH_IGNORE,
    );
  }
}

export function addTableDefinition(definition: TableDefinitionOutputV1) {
  const tableType = definition.tableType;
  const scalingTableTypes: TableTypeEnum[] = [TableTypeEnum.SCALING, TableTypeEnum.SCALINGCSV];
  const isScaling = scalingTableTypes.includes(tableType!);
  const currentDefinitions = isScaling ? sqWorkbookStore.tableDefinitions : sqWorkbookStore.itemTables;
  const isUpdate = currentDefinitions.some((def) => def.id === definition.id);
  const definitionAsPreview: ItemSearchPreviewV1 = {
    ...definition,
    type: SeeqNames.Types.TableDefinition,
    includedProperties: {
      [SeeqNames.Properties.TableType]: {
        name: SeeqNames.Properties.TableType,
        value: definition.tableType,
      },
    },
  };
  const newDefinitions = isUpdate
    ? currentDefinitions.map((def) => (def.id === definition.id ? definitionAsPreview : def))
    : [...currentDefinitions, definitionAsPreview];
  if (isScaling) {
    flux.dispatch('WORKBOOK_SET_TABLE_DEFINITIONS', { items: newDefinitions }, PUSH_IGNORE);
  } else {
    flux.dispatch('WORKBOOK_SET_ITEM_TABLES', { items: newDefinitions }, PUSH_IGNORE);
  }
}

/**
 * Makes asynchronous calls as required and loads the workbook.
 *
 * @param workbookId - The ID of the workbook to be loaded.
 * @param workbookDisplay - The view-mode for the workbook. One of WORKBOOK_DISPLAY
 * @returns {Promise} that resolves with the workbook data when the workbook is loaded.
 */
export function loadWorkbook(
  workbookId: string,
  workbookDisplay = WORKBOOK_DISPLAY.VIEW,
  onVantageWorkbookLoaded?: (workbook: VantageWorkbook) => void,
) {
  return getWorkbook(workbookId).then((workbook) => {
    workbook = _.assign(workbook, {
      isArchived: _.get(workbook, 'isArchived', false),
      workbookDisplay,
    });
    flux.dispatch('WORKBOOK_SET', workbook, PUSH_IGNORE);

    if (workbook.type === ITEM_TYPES.VANTAGE && onVantageWorkbookLoaded) {
      const { workbookConfig, evidenceTableDefinition } = workbook as StoreWorkbookWithWorksheets & VantageWorkbook;
      onVantageWorkbookLoaded({ workbookConfig, evidenceTableDefinition });
    }

    return workbook;
  });
}

/**
 * We don't normally fetch archived worksheets via [getWorkbook] for performance reasons, but if the current
 * worksheet is archived, then we need to fetch it so that we can display the name and other properties correctly.
 */
export async function loadArchivedWorksheet(workbookId: string, worksheetId: string) {
  const workbookWithArchivedWorksheets = await getWorkbook(workbookId, { includeArchivedWorksheets: true });
  const worksheet = workbookWithArchivedWorksheets.worksheets.find(
    (worksheet) => worksheet.worksheetId === worksheetId,
  );

  if (worksheet) {
    flux.dispatch('WORKBOOK_SET_ARCHIVED_WORKSHEET', { ...worksheet, isArchived: true }, PUSH_IGNORE);
  }
}

/**
 * Sets a workbook's or folder's name, removing any HTML markup, if name is less than maximum
 *
 * @param workbookId - The ID of the workbook or folder
 * @param name - The new name of the workbook or folder
 * @param originalName - The original name of the workbook or folder
 * @returns {Promise} A promise that resolves with the new name when the workbook or folder is updated.
 */
export function setWorkbookName(workbookId: string, name: string, originalName?: string) {
  if (name.length > MAX_NAME_LENGTH.WORKBOOK) {
    flux.dispatch('WORKBOOK_SET_PROP', { name: originalName }, PUSH_IGNORE);
    flux.dispatch('WORKBENCH_SET_NAME', { name: originalName, workbookId }, PUSH_IGNORE);
    warnToast({
      messageKey: 'NAME_LENGTH_ERROR',
      messageParams: { count: MAX_NAME_LENGTH.WORKBOOK },
    });
    return Promise.resolve();
  } else {
    // Rename locally immediately. If the server call fails, we'll revert the change.
    flux.dispatch('WORKBOOK_SET_PROP', { name }, PUSH_IGNORE);
    flux.dispatch('WORKBENCH_SET_NAME', { name, workbookId }, PUSH_IGNORE);

    return setProperty(SeeqNames.Properties.Name, workbookId, name)
      .then(() => {
        emitWorkbooks();
        emitWorkbook(workbookId);
      })
      .then(() => name)
      .catch(() => {
        warnToast({ messageKey: 'INVALID_NAME' });

        flux.dispatch('WORKBOOK_SET_PROP', { name: originalName }, PUSH_IGNORE);
        flux.dispatch(
          'WORKBENCH_SET_NAME',
          {
            name: originalName,
            workbookId,
          },
          PUSH_IGNORE,
        );
      });
  }
}

/**
 * Sets the workbook's description
 *
 * @param workbookId - The ID of the workbook
 * @param description - The description of the workbook
 * @returns {Promise} A promise that resolves when the workbook is updated.
 */
export function setWorkbookDescription(workbookId: string, description: string) {
  return setProperty('Description', workbookId, description)
    .then(() => {
      flux.dispatch('WORKBOOK_SET_PROP', { description }, PUSH_IGNORE);
    })
    .catch(() => warnToast({ messageKey: 'INVALID_NAME' }));
}

/**
 * Sets the project's projectType
 *
 * @param projectId   - The ID of the project
 * @param name        - The name of the project
 * @param projectType - The type of the project
 * @returns {Promise} A promise that resolves when the project is updated.
 */
export function updateProjectType(projectId: string, name: string, projectType: ProjectTypeEnum) {
  return setProjectType(projectId, name, projectType).catch(() =>
    warnToast({ messageKey: 'WORKBOOK_ERROR_UPDATING_PROJECT_TYPE' }),
  );
}

/**
 * Sets the workbook's display mode
 *
 * @param workbookDisplay - The display mode of the workbook
 */
export function setWorkbookDisplayMode(workbookDisplay: WORKBOOK_DISPLAY) {
  flux.dispatch('WORKBOOK_SET_PROP', { workbookDisplay }, PUSH_IGNORE);
}

/**
 * Creates a new worksheet in a workbook
 *
 * @param workbookId - ID of workbook in which to create a new worksheet
 * @param options - Additional options.
 * @param {String} [options.name] - Optional name for the worksheet. If no name is provided, a name is
 *   automatically generated.
 * @param {String} [options.prefix] - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 * @returns {Promise} A promise that resolves with the worksheet when the worksheet has been added
 */
export function addWorksheetToWorkbook(workbookId: string, options?: object) {
  clearWorkstepAction();
  return createWorksheet(workbookId, computeWorksheetName(options)).then((worksheet) => {
    // Add worksheet to store
    flux.dispatch('WORKBOOK_ADD_WORKSHEET', { worksheet }, PUSH_IGNORE);

    emitWorkbook(workbookId);
    return worksheet;
  });
}

/**
 * Creates a branch of a worksheet and adds it as the last worksheet in the workbook
 *
 * @param workbookId - ID of the workbook on which the worksheet exists
 * @param worksheetId - ID of the worksheet to branch
 * @param itemIdsToClone - All items from details panel to create their copies
 * @param namePrefix - Prefix for the names of the newly created items
 * @param nameSuffix - Suffix for the name of the newly created items
 * @param options - Additional options.
 * @param isCloningToTheSameWorkbook - if true cloning worksheet to the same workbook
 * @param {String} [options.name] - Optional name for the worksheet. If no name is provided, a name is
 *   automatically generated.
 * @param {String} [options.prefix] - Optional prefix for the auto-generated worksheet name. If no prefix is
 *   provided, the name is the next number.
 */
export async function branchWorksheet(
  workbookId: string,
  worksheetId: string,
  options: object,
  itemIdsToClone?: string[],
  namePrefix?: string,
  nameSuffix?: string,
  isCloningToTheSameWorkbook = true,
) {
  try {
    if (sqWorkbookStore.getWorksheet(worksheetId)?.isDashboard) {
      errorToast({ messageKey: 'DASHBOARD.COPY_NOT_SUPPORTED' });
      doTrack('Topic', 'attempt to copy Dashboard');
      return null;
    }
    const name = computeWorksheetName(options) || DEFAULT_WORKSHEET_NAME;
    const { data: rawWorksheet } = await sqWorkbooksApi.createWorksheet(
      {
        name,
        branchFrom: worksheetId,
        itemIdsToClone,
        cloneNamePrefix: namePrefix,
        cloneNameSuffix: nameSuffix,
        actionBoardTimezone: sqWorkbenchStore.timezone?.name,
      },
      { workbookId },
    );
    const worksheet = toWorksheet(rawWorksheet, false);
    emitWorkbook(workbookId);
    if (isCloningToTheSameWorkbook) {
      flux.dispatch('WORKBOOK_ADD_WORKSHEET', { worksheet }, PUSH_IGNORE);
      getNavigate()(getWorksheetLink(workbookId, worksheet?.worksheetId));
    } else {
      successToast({
        messageKey: 'HOME_SCREEN.DUPLICATION_SUCCESS',
        messageParams: { ITEM_NAME: worksheet.name },
        buttonLabelKey: 'OPEN',
        buttonAction: () => window.open(getWorksheetLink(workbookId, worksheet?.worksheetId), '_blank'),
        buttonIcon: null,
      });
    }
    return worksheet;
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return null;
  }
}

/**
 * Restores a worksheet
 *
 * @param workbookId - ID of the workbook on which the worksheet exists
 * @param worksheetId - ID of the worksheet to branch
 * @param worksheetIndex - position where the worksheet will be restored
 * @returns {Promise} a promise that resolves with the restored worksheet
 */
export function restoreWorksheet(workbookId: string, worksheetId: string, worksheetIndex: number) {
  return restoreWorksheetUtils(worksheetId)
    .then(() => getWorksheet(sqWorkbenchStore.stateParams.workbookId, worksheetId))
    .then((worksheet) => {
      // Re-add worksheet to store
      flux.dispatch(
        'WORKBOOK_ADD_WORKSHEET',
        {
          worksheet,
          position: worksheetIndex,
        },
        PUSH_IGNORE,
      );

      emitWorkbook(workbookId);
      return worksheet;
    });
}

/**
 * Sets the name on a worksheet, removing any HTML markup, if name is less than maximum
 *
 * @param worksheetId - GUID for the worksheet to rename
 * @param name - Name to set on the worksheet
 * @return {Promise} that resolves with the new name when the worksheet has been renamed
 */
export function renameWorksheet(worksheetId: string, name: string) {
  const oldName = sqWorkbookStore.getWorksheetName(worksheetId);

  if (name.length > MAX_NAME_LENGTH.WORKSHEET) {
    setWorksheetProperty(worksheetId, 'name', oldName);
    warnToast({
      messageKey: 'NAME_LENGTH_ERROR',
      messageParams: { count: MAX_NAME_LENGTH.WORKSHEET },
    });
    return Promise.resolve(false);
  } else {
    // Rename worksheet locally immediately. If the server call fails, we'll revert the change.
    flux.dispatch('WORKBOOK_RENAME_WORKSHEET', { worksheetId, name }, PUSH_IGNORE);

    return setNameUtil(worksheetId, name)
      .then(() => {
        emitWorkbook(sqWorkbookStore.workbookId);
        return name;
      })
      .catch(() => {
        flux.dispatch(
          'WORKBOOK_RENAME_WORKSHEET',
          {
            worksheetId,
            name: oldName,
          },
          PUSH_IGNORE,
        );
        warnToast({ messageKey: 'INVALID_NAME' });

        return Promise.resolve(false);
      });
  }
}

/**
 * Set a property on one of the worksheets in the current workbook. This adds or changes the property in the
 * sqWorkbookStore.worksheets array.
 *
 * @param worksheetId - ID for the worksheet in the current workbook
 * @param propertyName - Name of property to set on the specified worksheet
 * @param propertyValue - Value of the property to set
 */
export function setWorksheetProperty(
  worksheetId: string,
  propertyName: string,
  propertyValue: string | boolean | undefined,
) {
  flux.dispatch(
    'WORKBOOK_SET_WORKSHEET_PROPERTY',
    {
      worksheetId,
      propertyName,
      propertyValue,
    },
    PUSH_IGNORE,
  );
}

/**
 * Moves a worksheet to a new location in the specified workbook. If the call to move the worksheet on the server
 * fails, the move is reverted.
 *
 * @param workbookId - Workbook ID that contains the worksheet
 * @param worksheet - Worksheet to move
 * @param worksheet.worksheetId - ID of the worksheet to be moved
 * @param nextWorksheet - Worksheet marking the destination location
 * @param nextWorksheet.worksheetId - ID of the worksheet marking the destination location
 * @return {Promise} A promise that resolves when the worksheet has been moved
 */
export function moveWorksheetInWorkbook(
  workbookId: string,
  worksheet: {
    worksheetId: string;
  },
  nextWorksheet?: {
    worksheetId: string;
  },
) {
  const currentIndex = _.findIndex(sqWorkbookStore.worksheets, ['worksheetId', worksheet.worksheetId]);
  const oldNextId = sqWorkbookStore.worksheets[currentIndex + 1]?.worksheetId;
  const newNextId = nextWorksheet?.worksheetId;

  // Change the store now, assuming the service call succeeds. If it doesn't, we'll revert the change.
  flux.dispatch(
    'WORKBOOK_MOVE_WORKSHEET',
    {
      worksheetId: worksheet.worksheetId,
      nextWorksheetId: newNextId,
    },
    PUSH_IGNORE,
  );

  // Commit the change to the server
  return moveWorksheetUtil(workbookId, worksheet.worksheetId, newNextId).catch((response) => {
    flux.dispatch(
      'WORKBOOK_MOVE_WORKSHEET',
      {
        worksheetId: worksheet.worksheetId,
        nextWorksheetId: oldNextId,
      },
      PUSH_IGNORE,
    );

    errorToast({ httpResponseOrError: response });
  });
}

/**
 * Archives a worksheet.
 *
 * @param workbookId - ID of the workbookId the worksheet belongs to
 * @param worksheetId - ID of the worksheet to be archived
 */
export function archiveWorksheet(workbookId: string, worksheetId: string) {
  return archiveWorksheetUtil(worksheetId).then(() => {
    let newSelectedIndex;
    const index = _.findIndex(sqWorkbookStore.worksheets, { worksheetId });

    // Worksheet already removed from store
    if (index < 0) {
      return;
    }

    // If the selected worksheet was archived (removed), then choose the previous worksheet as the new selected
    // worksheet
    if (worksheetId === sqWorkbookStore.worksheets[index].worksheetId) {
      newSelectedIndex = Math.min(Math.max(index - 1, 0), sqWorkbookStore.worksheets.length - 1);
    }

    // Remove worksheet from store
    flux.dispatch('WORKBOOK_REMOVE_WORKSHEET', { worksheetId }, PUSH_IGNORE);

    // If there are worksheets and the selected worksheet was archived (removed), then route to another worksheet
    if (sqWorkbookStore.worksheets.length > 0) {
      if (!_.isUndefined(newSelectedIndex)) {
        getNavigate()(getWorksheetLink(workbookId, sqWorkbookStore.worksheets[newSelectedIndex].worksheetId));
      }
    } else {
      // If there isn't a worksheet in the workbook, then add one
      addWorksheetToWorkbook(workbookId).then(function (worksheet) {
        getNavigate()(getWorksheetLink(workbookId, worksheet.worksheetId));
      });
    }

    emitWorkbook(workbookId);
  });
}

/**
 * Adds an item to the pinned list for the specified workbook and then updates the pinned list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item to be pinned
 * @return {Promise} Resolves when pin is complete
 */
export function pinToWorkbook(workbookId: string, id: string) {
  return sqItemsApi.pinItem({ sourceId: workbookId, itemId: id }).then(({ data }) => {
    addAssetsProperty(data);
    const items = data.items ?? [];
    fetchAndAddTableTypePropertyToItems(items).then((updatedItems) => {
      flux.dispatch('WORKBOOK_SET_PROP', { pinned: updatedItems }, PUSH_IGNORE);
    });
  });
}

/**
 * Removes an item from the pinned list for the specified workbook and then updates the pinned list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item to be unpinned
 * @return {Promise} Resolves when unpin is complete
 */
export function unpinToWorkbook(workbookId: string, id: string) {
  return sqItemsApi.unpinItem({ sourceId: workbookId, itemId: id }).then(({ data }) => {
    addAssetsProperty(data);
    flux.dispatch('WORKBOOK_SET_PROP', { pinned: data.items }, PUSH_IGNORE);
  });
}

/**
 * Adds an item to the recently accessed list for the specified workbook and then updates the recently accessed list
 * with the latest.
 *
 * @param workbookId - The ID of the workbook
 * @param id - The ID of the item that was recently accessed
 */
export function addRecentlyAccessed(workbookId: string, id: string) {
  if (isEditWorkbookMode()) {
    addRecentlyAccessedHomescreen(workbookId, id)
      .then((data: any) => flux.dispatch('WORKBOOK_SET_PROP', { recentlyAccessed: data.items }, PUSH_IGNORE))
      // Ignore backend errors (for example when adding an annotation with only read permissions to the
      // workbook/signal)
      .catch(_.noop);
  }
}

/**
 * Executes the set of actions to change the view for a worksheet in viewing mode. The comment tab is selected and
 * the left panel is then hidden. The bottom panels are also reset to the default height.
 */
export function resetViewingState() {
  // Tools like Manual Condition can still effect the look of the trend in presentation mode, close them
  closeInvestigationTool();
}

/**
 * A helper function that sets up websocket workbook notification
 */
export function setupNotifierForWorkbook(onVantageWorkbookLoaded?: (workbook: VantageWorkbook) => void) {
  onWorkbook((workbookId: string) => {
    if (workbookId === sqWorkbenchStore.stateParams.workbookId) {
      loadWorkbook(workbookId, sqWorkbookStore.workbookDisplay, onVantageWorkbookLoaded);
    }
  });
}

/**
 * Changes the item owner to the one specified.
 *
 * @param itemId - The ID of the item (workbook or folder) to change owner of
 * @param user - The new owner
 * @param user.id - The ID of the new owner
 * @param user.name - The name of the new owner
 * @returns {Promise} A promise that resolves when the workbook owner has been updated.
 */
export function setItemOwner(
  itemId: string,
  user: {
    id: string;
    name: string;
  },
) {
  return sqItemsApi
    .changeOwner({ itemId, newOwnerId: user.id })
    .then(() => flux.dispatch('WORKBOOK_SET_OWNER', user, PUSH_IGNORE));
}

/**
 * Changes the topic renderer to the one specified.
 *
 * @param itemId - The ID of the topic to change renderer of
 * @param user - The new renderer
 * @param user.id - The ID of the new renderer
 * @param user.name - The name of the new renderer
 * @returns {Promise} A promise that resolves when the workbook renderer has been updated.
 */
export function setItemRenderer(
  itemId: string,
  user: {
    id: string;
    name: string;
  },
) {
  return sqWorkbooksApi.setRenderer({ id: itemId, userId: user.id }).then(() => {
    flux.dispatch('REPORT_SET_RENDERER', user, PUSH_IGNORE);
  });
}

export function resetWorkbook() {
  flux.dispatch('WORKBOOK_RESET', undefined);
}

/**
 * Changes the viewers list to the one specified in the payload.
 *
 * @param payload The websocket message containing a list of viewers
 * @param payload.subscribers A list of subscribers to this workbook.
 */
export function setViewers(payload: { subscriberParameters: object }) {
  flux.dispatch('WORKBOOK_SET_VIEWERS', payload.subscriberParameters, PUSH_IGNORE);
}

export function resetWorkbookType() {
  flux.dispatch('WORKBOOK_SET_TYPE', { type: undefined }, PUSH_IGNORE);
}

export function setLockedChangeMade(changeMade = true) {
  flux.dispatch('WORKBOOK_SET_LOCKED_CHANGE_MADE', { lockedChangeMade: changeMade }, PUSH_IGNORE);
}

export function setLockingErrors(lockingErrors: LockErrorOutputV1[]): void {
  flux.dispatch('WORKBOOK_SET_LOCKING_ERRORS', { lockingErrors }, PUSH_WORKBENCH);
}
